/**
 * StatBlockV1 and AttackV1 (P0-4b deliverable 2): a complete 2014 stat block
 * in printed order, for SRD creatures, homebrew monsters and imported NPCs.
 *
 * Frozen by 1.0b stage 2 (`DND5E_SCHEMA_STATUS`, `STATBLOCK_SCHEMA_VERSION`): from now on a change
 * is additive only, through a contract steward round. P0-4b's open questions for the freeze are
 * settled without changing a shape: `state` stays here, null for every source but a Foundry
 * import; `legendaryResistance` stays a count, its text kept as a trait; the nine null-allow
 * entries of the SRD coverage table stand; and a flatter generation schema, if Phase 3's
 * `/statblock` generation needs one, maps into StatBlockV1.
 */
import { Ability, AcEntry, allDistinct, ChallengeRating, Condition, CreatureType, DamagePart, DamageTraitGroup, Dice, Die, FeatureId, RulesVersion, Size, Skill, SourceMeta, Text, } from './common.js';
import { z } from './zod.js';
// ------------------------------------------------------------------ attack
export const ATTACK_RANGES = ['melee', 'ranged', 'meleeOrRanged'];
export const ATTACK_KINDS = ['weapon', 'spell'];
/**
 * Every 2014 attack line: "Melee Weapon Attack: +6 to hit, reach 5 ft., one
 * target. Hit: 9 (1d10 + 4) slashing damage, or 10 (1d12 + 4) slashing damage
 * if used with two hands, plus 7 (2d6) fire damage." Distances are in feet.
 * P0-11b's attack-line parser returns this shape.
 */
export const AttackV1 = z.strictObject({
    range: z.enum(ATTACK_RANGES),
    kind: z.enum(ATTACK_KINDS),
    toHit: z.number().int(),
    reach: z.number().int().min(0).nullable(),
    rangeNormal: z.number().int().min(0).nullable(),
    rangeLong: z.number().int().min(0).nullable(),
    /** As printed: "one target", "one creature", "one target in the swarm's space". */
    target: z.string(),
    /** The hit damage parts, before any "plus". */
    damage: z.array(DamagePart),
    /** "or 10 (1d12 + 4) slashing damage if used with two hands". */
    versatile: DamagePart.nullable(),
    /** "plus 7 (2d6) fire damage". */
    riders: z.array(DamagePart),
    /** The rest of the hit clause: "and the target is grappled (escape DC 13)." */
    hitText: z.string().nullable(),
});
// ----------------------------------------------------------------- feature
export const REST_KINDS = ['short', 'long'];
/** "Recharge 5–6" is `{kind: 'recharge', from: 5}`; "Recharge 6" is `from: 6`. */
export const RechargeUsage = z.strictObject({
    kind: z.literal('recharge'),
    from: z.number().int().min(2).max(6),
});
/** "3/Day". */
export const PerDayUsage = z.strictObject({
    kind: z.literal('perDay'),
    count: z.number().int().min(1),
});
/** "Recharges after a Short or Long Rest" is `{rest: 'short'}`; "after a Long Rest" is `long`. */
export const PerRestUsage = z.strictObject({
    kind: z.literal('perRest'),
    count: z.number().int().min(1),
    rest: z.enum(REST_KINDS),
});
export const Usage = z.union([RechargeUsage, PerDayUsage, PerRestUsage]);
/** A trait, action, bonus action, reaction or lair action. `text` is CommonMark. */
export const Feature = z.strictObject({
    id: FeatureId,
    name: Text,
    text: z.string(),
    usage: Usage.nullable(),
    attack: AttackV1.nullable(),
});
export const LegendaryOption = z.strictObject({
    id: FeatureId,
    name: Text,
    text: z.string(),
    /** "Wing Attack (Costs 2 Actions)" has cost 2. */
    cost: z.number().int().min(1),
    attack: AttackV1.nullable(),
});
export const Legendary = z.strictObject({
    /** "The dragon can take 3 legendary actions, choosing from the options below. …" */
    intro: z.string(),
    perRound: z.number().int().min(1),
    options: z.array(LegendaryOption).min(1),
});
export const Lair = z.strictObject({
    /** The initiative count lair actions take place on (normally 20). */
    initiative: z.number().int(),
    /** Whether the creature is met in its lair, which adds a legendary use in dnd5e 5.x. */
    inside: z.boolean(),
    intro: z.string().nullable(),
    actions: z.array(Feature).min(1),
});
// ------------------------------------------------------------ spellcasting
/** A spell named in a spellcasting block; it links to a spell card by name. */
export const SpellRef = z.strictObject({
    /** A spell entry carries its own id, like every feature (an NPC's spell is an item in Foundry). */
    id: FeatureId,
    name: Text,
    /** A mark printed beside the name: "(self only)", "*". */
    note: z.string().nullable(),
});
export const SpellsAtLevel = z.strictObject({
    /** 0 is cantrips. */
    level: z.number().int().min(0).max(9),
    /** "1st level (4 slots)"; null for cantrips. */
    slots: z.number().int().min(0).nullable(),
    spells: z.array(SpellRef).min(1),
});
export const SpellsPerDay = z.strictObject({
    /** null is "At will". */
    perDay: z.number().int().min(1).nullable(),
    /** "3/day each". */
    each: z.boolean(),
    spells: z.array(SpellRef).min(1),
});
export const SPELLCASTING_KINDS = ['innate', 'class'];
export const Spellcasting = z
    .strictObject({
    id: FeatureId,
    name: Text,
    kind: z.enum(SPELLCASTING_KINDS),
    ability: Ability,
    saveDc: z.number().int().min(0).nullable(),
    attackBonus: z.number().int().nullable(),
    /** "is a 9th-level spellcaster"; dnd5e's `attributes.spell.level`. */
    casterLevel: z.number().int().min(1).max(20).nullable(),
    /** "wizard spells". */
    spellList: z.string().nullable(),
    /** The introduction as printed, CommonMark. */
    text: z.string(),
    byLevel: z.array(SpellsAtLevel),
    perDay: z.array(SpellsPerDay),
})
    .refine((s) => s.byLevel.length + s.perDay.length > 0, {
    message: 'A spellcasting block lists spells by level or per day',
    path: ['byLevel'],
});
// --------------------------------------------------------------- stat block
export const AbilityScores = z.strictObject({
    str: z.number().int().min(1).max(30),
    dex: z.number().int().min(1).max(30),
    con: z.number().int().min(1).max(30),
    int: z.number().int().min(1).max(30),
    wis: z.number().int().min(1).max(30),
    cha: z.number().int().min(1).max(30),
});
export const SaveBonus = z.strictObject({ ability: Ability, bonus: z.number().int() });
export const SkillBonus = z.strictObject({ skill: Skill, bonus: z.number().int() });
export const Speed = z.strictObject({
    walk: z.number().int().min(0).nullable(),
    fly: z.number().int().min(0).nullable(),
    swim: z.number().int().min(0).nullable(),
    climb: z.number().int().min(0).nullable(),
    burrow: z.number().int().min(0).nullable(),
    hover: z.boolean(),
    /** Anything else printed on the speed line. */
    note: z.string().nullable(),
});
export const Senses = z.strictObject({
    blindsight: z.number().int().min(0).nullable(),
    darkvision: z.number().int().min(0).nullable(),
    tremorsense: z.number().int().min(0).nullable(),
    truesight: z.number().int().min(0).nullable(),
    /** "blind beyond this radius". */
    blindBeyond: z.boolean(),
    passivePerception: z.number().int().min(0),
    note: z.string().nullable(),
});
export const Languages = z.strictObject({
    /** As printed, in order: "Common", "Deep Speech". */
    values: z.array(Text),
    telepathy: z.number().int().min(0).nullable(),
    /** "understands Common but can't speak", "—". */
    note: z.string().nullable(),
});
export const CreatureTypeInfo = z.strictObject({
    value: CreatureType,
    /** "(goblinoid)", "(any race)". */
    subtype: z.string().nullable(),
    /** A swarm of Tiny beasts has `swarmSize: 'tiny'`. */
    swarmSize: Size.nullable(),
    custom: z.string().nullable(),
});
/** Tracking state carried from a Foundry import so that an export restores it; null otherwise. */
export const StatBlockState = z.strictObject({
    hitPoints: z.number().int().nullable(),
    tempHitPoints: z.number().int().nullable(),
    tempMaxHitPoints: z.number().int().nullable(),
    legendaryActionsSpent: z.number().int().min(0),
    legendaryResistancesSpent: z.number().int().min(0),
    hitDiceSpent: z.number().int().min(0),
});
export const HabitatEntry = z.strictObject({ type: Text, subtype: z.string().nullable() });
export const StatBlockV1 = z
    .strictObject({
    schemaVersion: z.literal(1),
    source: SourceMeta,
    rules: RulesVersion.nullable(),
    name: Text,
    size: Size,
    creatureType: CreatureTypeInfo,
    /** As printed: "chaotic evil", "any non-good alignment", "unaligned". */
    alignment: z.string(),
    armorClass: z.array(AcEntry).min(1),
    hitPoints: z.strictObject({
        average: z.number().int().min(0),
        /** "(4d8 + 4)". */
        formula: Dice.nullable(),
    }),
    /** The hit dice: the formula's dice plus any class levels. */
    hitDice: z.strictObject({ count: z.number().int().min(0), die: Die }).nullable(),
    speed: Speed,
    abilities: AbilityScores,
    /** Explicit printed bonuses, in printed order. */
    savingThrows: z.array(SaveBonus).refine((list) => allDistinct(list.map((s) => s.ability)), {
        message: 'Each ability appears once',
    }),
    skills: z.array(SkillBonus).refine((list) => allDistinct(list.map((s) => s.skill)), {
        message: 'Each skill appears once',
    }),
    damageVulnerabilities: z.array(DamageTraitGroup),
    damageResistances: z.array(DamageTraitGroup),
    damageImmunities: z.array(DamageTraitGroup),
    conditionImmunities: z.array(Condition),
    senses: Senses,
    languages: Languages,
    challenge: z.strictObject({
        rating: ChallengeRating,
        xp: z.number().int().min(0),
        /** The XP in its lair, where the printed block gives one. */
        lairXp: z.number().int().min(0).nullable(),
    }),
    proficiencyBonus: z.number().int().min(2).max(9),
    initiativeBonus: z.number().int().nullable(),
    traits: z.array(Feature),
    actions: z.array(Feature),
    bonusActions: z.array(Feature),
    reactions: z.array(Feature),
    legendary: Legendary.nullable(),
    /** "Legendary Resistance (3/Day)": the uses per day; its text is a trait. */
    legendaryResistance: z.number().int().min(1).nullable(),
    lair: Lair.nullable(),
    spellcasting: z.array(Spellcasting),
    habitat: z.array(HabitatEntry),
    treasure: z.array(Text),
    state: StatBlockState.nullable(),
})
    .superRefine((block, ctx) => {
    const lists = [
        [['traits'], block.traits],
        [['actions'], block.actions],
        [['bonusActions'], block.bonusActions],
        [['reactions'], block.reactions],
        [['legendary', 'options'], block.legendary?.options ?? []],
        [['lair', 'actions'], block.lair?.actions ?? []],
        [['spellcasting'], block.spellcasting],
        ...block.spellcasting.flatMap((s, i) => [
            ...s.byLevel.map((l, j) => [
                ['spellcasting', i, 'byLevel', j, 'spells'],
                l.spells,
            ]),
            ...s.perDay.map((d, j) => [
                ['spellcasting', i, 'perDay', j, 'spells'],
                d.spells,
            ]),
        ]),
    ];
    const seen = new Set();
    for (const [path, list] of lists) {
        for (const [i, feature] of list.entries()) {
            if (seen.has(feature.id)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Every feature carries its own id',
                    path: [...path, i, 'id'],
                });
            }
            seen.add(feature.id);
        }
    }
});
//# sourceMappingURL=statblock.js.map